import { NavigateFunction } from 'react-router-dom'
import type { Command } from './commands'
import type { GlobalSearchResults } from '../hooks/useGlobalSearch'
import type { TerminologyConfig } from './terminology'

/**
 * F8 — Global entity search.
 *
 * Converts live cross-entity search results into Command-palette entries so the
 * Cmd+K palette can jump directly to any named Failure Mode / Root Cause /
 * Mitigation / Component / Requirement across the FMEA graph.
 *
 * The app has no per-entity deep-link detail routes, so selecting a result
 * navigates to the owning list/workshop page and pre-fills that page's search
 * box (the standard `[data-search-input]` hook) with the entity's identifying
 * text, mirroring the existing "Find Component" behaviour.
 */

// Max results surfaced per entity type in the palette (kept small + bounded).
export const ENTITY_RESULTS_PER_TYPE = 6

const PAGE_ROUTES = {
  component: '/components',
  failureMode: '/cause-effect',
  rootCause: '/causes-workshop',
  mitigation: '/mitigations',
  requirement: '/requirements',
} as const

/**
 * Locate the target page's primary search box. Prefers the explicit
 * `[data-search-input]` hook (the app convention), then falls back to a
 * visible search-style input so filtering works on pages that expose an
 * ordinary search field without the data attribute.
 */
function findPageSearchInput(): HTMLInputElement | null {
  const explicit = document.querySelector(
    '[data-search-input]'
  ) as HTMLInputElement | null
  if (explicit) return explicit

  const candidates = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'input[type="search"], input[placeholder*="earch"], input[aria-label*="earch"]'
    )
  )
  // Skip the command palette's own input and any hidden fields.
  return (
    candidates.find(
      (el) =>
        !el.closest('.command-palette') &&
        el.offsetParent !== null &&
        !el.disabled
    ) || null
  )
}

/**
 * Navigate to a page and pre-fill its search box so the chosen entity is
 * immediately visible/filtered. Uses the native value setter + input event so
 * React-controlled inputs pick up the change.
 */
function navigateToEntity(
  navigate: NavigateFunction,
  route: string,
  filterText: string
): void {
  navigate(route)

  const applyValue = (input: HTMLInputElement) => {
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set
    if (nativeSetter) {
      nativeSetter.call(input, filterText)
      input.dispatchEvent(new Event('input', { bubbles: true }))
    } else {
      input.value = filterText
    }
  }

  // Lazy-loaded pages mount asynchronously and may re-initialise their own
  // search state after mount, so (a) poll for the input, then (b) re-apply a
  // few times to win the race against that late state reset.
  let findAttempts = 0
  let reapplies = 0
  const tick = () => {
    const input = findPageSearchInput()
    if (!input) {
      if (findAttempts++ < 40) setTimeout(tick, 150)
      return
    }
    if (input.value !== filterText) applyValue(input)
    input.focus()
    if (reapplies === 0) input.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // Keep re-applying briefly to survive post-mount resets.
    if (reapplies++ < 8) setTimeout(tick, 250)
  }
  setTimeout(tick, 150)
}

function truncate(text: string, max = 90): string {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/**
 * Build palette commands from global search results.
 * `t` supplies terminology-aware group headings (FMEA vs HAZID).
 */
export function buildEntitySearchCommands(
  results: GlobalSearchResults | undefined,
  navigate: NavigateFunction,
  t: TerminologyConfig
): Command[] {
  if (!results) return []

  const commands: Command[] = []

  // Components (naming is fixed — not terminology-mapped)
  results.components.slice(0, ENTITY_RESULTS_PER_TYPE).forEach((c) => {
    const path = c.path && c.path.length > 1 ? c.path.join(' › ') : c.type
    commands.push({
      id: `entity-component-${c.id}`,
      label: c.name,
      description: truncate(path || c.description || ''),
      category: 'Components',
      icon: '🔧',
      execute: () =>
        navigateToEntity(navigate, PAGE_ROUTES.component, c.name),
    })
  })

  // Failure Modes / Hazards
  results.failureModes.slice(0, ENTITY_RESULTS_PER_TYPE).forEach((f) => {
    const comps = f.components.map((c) => c.name).join(', ')
    const meta = [comps, f.rpn != null ? `${t.rpn} ${f.rpn}` : '']
      .filter(Boolean)
      .join(' · ')
    commands.push({
      id: `entity-failure-${f.id}`,
      label: truncate(f.description),
      description: meta || undefined,
      category: t.failureModes,
      icon: '⚡',
      execute: () =>
        navigateToEntity(navigate, PAGE_ROUTES.failureMode, f.description),
    })
  })

  // Root Causes
  results.rootCauses.slice(0, ENTITY_RESULTS_PER_TYPE).forEach((rc) => {
    const linked = rc.linkedFailureModes.length
    commands.push({
      id: `entity-cause-${rc.id}`,
      label: truncate(rc.description),
      description:
        linked > 0
          ? `${linked} linked ${linked === 1 ? t.failureMode : t.failureModes}`
          : undefined,
      category: t.rootCauses,
      icon: '🌱',
      execute: () =>
        navigateToEntity(navigate, PAGE_ROUTES.rootCause, rc.description),
    })
  })

  // Mitigations / Controls
  results.mitigations.slice(0, ENTITY_RESULTS_PER_TYPE).forEach((m) => {
    const meta = [
      m.status,
      m.failureMode ? truncate(m.failureMode.description, 40) : '',
    ]
      .filter(Boolean)
      .join(' · ')
    commands.push({
      id: `entity-mitigation-${m.id}`,
      label: truncate(m.description),
      description: meta || undefined,
      category: t.mitigations,
      icon: '🛡️',
      execute: () =>
        navigateToEntity(navigate, PAGE_ROUTES.mitigation, m.description),
    })
  })

  // Requirements / Client Requirements
  results.requirements.slice(0, ENTITY_RESULTS_PER_TYPE).forEach((r) => {
    commands.push({
      id: `entity-requirement-${r.id}`,
      label: `${r.requirementId} — ${truncate(r.title, 70)}`,
      description: truncate(r.description),
      category: t.requirements,
      icon: '📝',
      execute: () =>
        navigateToEntity(navigate, PAGE_ROUTES.requirement, r.requirementId),
    })
  })

  return commands
}

/**
 * Ordered, de-duplicated list of the entity group headings that actually have
 * results, so the palette renders them in a stable, sensible order.
 */
export function entitySearchCategories(commands: Command[]): string[] {
  const seen = new Set<string>()
  const order: string[] = []
  commands.forEach((c) => {
    if (!seen.has(c.category)) {
      seen.add(c.category)
      order.push(c.category)
    }
  })
  return order
}
